import React, { useContext, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import 'react-responsive-modal/styles.css';
import { Modal } from 'react-responsive-modal';

import Checkbox from 'commons/components/Checkbox';
import MetsIcon from 'commons/components/MetsIcon';

import ConfigurationContext from 'UserRegistration/contexts/ConfigurationContext';
import DataContext from 'UserRegistration/contexts/DataContext';
import SubscriptionPackagePerksList from '../SubscriptionPackagePerksList';
import ItemSelect from 'commons/components/ItemSelect';
import { formatPrice } from 'commons/resources/utils/numeric';

const DEFAULT_PERK_CODE = "DEFAULT";

export interface SubscriptionItem {
    
    agenciesCount: string,

    associatedItemKey: string,

    id: number,
    
    description: string,
    
    itemCode: string,
    
    purchasingGroupId: number,
    
    enabled: boolean,
    
    sequence: number,
    
    preselected: boolean,

    value: any
}

interface SubscriptionPackageProps {
    /** The billing frequency to use when displaying the package. */
    frequency: string,
    /** onClick handler function that should handle what happens when the user selects or chooses a package. */
    onClickHandler: () => {},
    /** The subscription package object that contains all necessary data. Maps to AvailableSubscriptionItemGroupDto in mets-app */
    subscriptionPackage: any,
    /** Flag if the package selector is selected */
    isSelected: boolean,
    /** Flag if the group should render the subscription item select */
    displaySubscriptionItemSelect: boolean,
    /** Indicates whether or not the package is selectable or not */
    disabled: boolean
}


function getSelectedAgenciesCount(packagePrice: any, subscriptionPackage: any, registrationData: any) {

    let selectedAgenciesCount: number | null = null;
    let selectedAssociatedItemKeys: string[] = registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionAssociatedItemKeys;

    // We check for a null package price because if there is none it means it's free and therefore there is nothing to display
    if (packagePrice && selectedAssociatedItemKeys && selectedAssociatedItemKeys.length > 0) {

        let selectedItems: SubscriptionItem[] = subscriptionPackage.subscriptionItems.
            filter((item: SubscriptionItem) => selectedAssociatedItemKeys.includes(item.associatedItemKey));

        selectedItems.forEach(item => {
            if (item.agenciesCount != null && item.enabled) {
                selectedAgenciesCount ? selectedAgenciesCount += Number(item.agenciesCount) : selectedAgenciesCount = +item.agenciesCount
            }
        });
    
   
    }

    return selectedAgenciesCount;
}


/**
 * Gets the perk list code to use for this group or "DEFAULT" if we have multiple different codes
 * @param subscriptionItemGroup 
 * @param currentPerkListCode 
 * @param subscriptionItem 
 * @returns 
 */
function getPerkListCodeOrDefault(subscriptionItemGroup:any, currentPerkListCode:string, subscriptionItem:SubscriptionItem) {

    const subItemCode = subscriptionItem.itemCode;
    
    let newPerkListCode = subscriptionItemGroup.perkListMap[subItemCode];

    if (newPerkListCode) {

         // Use the new perk list code if we don't already have a perk list OR if this new one is the same as the current one
        if (currentPerkListCode == "" || (currentPerkListCode == newPerkListCode)) {
            return newPerkListCode;
        }
    }

    // otherwise use the default code
    return subscriptionItemGroup.perkListMap[DEFAULT_PERK_CODE];
}

/**
 * Get a code to identify the appropriate list of perks to be displayed
 * @param {*} registrationData 
 * @returns The internalName of the selected subscription item that has a custom perk list, or "DEFAULT"
 */
 function getAppropriatePerkListCode(subscriptionItemGroup: any, registrationData: any) {

    let perkListCode = "";

    // loop through all items in this group
    subscriptionItemGroup.subscriptionItems.forEach((subscriptionItem: SubscriptionItem) => {

        // Check if any of this groups subscription items are in our selected list...
        if (registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds.includes(subscriptionItem.id) 
            || registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionAssociatedItemKeys.includes(subscriptionItem.associatedItemKey)) {

            perkListCode = getPerkListCodeOrDefault(subscriptionItemGroup, perkListCode, subscriptionItem);
        }

    });
        
    // if not custom perk list was found, use the default perk list key
    if (perkListCode == "" && DEFAULT_PERK_CODE in subscriptionItemGroup.perkListMap) {
        perkListCode = subscriptionItemGroup.perkListMap[DEFAULT_PERK_CODE];
    }

    return perkListCode;
    
}


/**
 * Find the item code for the currently selected subscription item (or "DEFAULT" if none selected)
 * Fetch the appropriate perk list key from the perkListMap and retrieve the list of perks for that map
 * @returns An array of strings for SubsciptionpackagePerksList
 */
export function getPerkList(subscriptionItemGroup: any, registrationData: any, t: any) {

    // get the appropriate key for the perk list map
    const perkListCode = getAppropriatePerkListCode(subscriptionItemGroup, registrationData);

    if (perkListCode) {

        // Get the string list of perks
        const perkItemsString = t("supplier.services.group.perkList." + perkListCode);

        if (perkItemsString.length) {
            // return the pipe-delimited string split into an array 
            return perkItemsString.split("|");
        }

    }

    return [];
}

/**
 * Renders the tiles name at the top.
 * 
 * @param subscriptionPackage - The subscription package we are rendering the title for.
 * @param t - i18n for language translation
 * @oaram componentConfig - settings and configuration
 */
 function renderPackageTileName(subscriptionPackage: any, t: any, componentConfig: any) {

    if (!componentConfig.settings.enableABTesting || componentConfig.settings.enableABTesting == false) {
        return subscriptionPackage.cmsKey && t(subscriptionPackage.cmsKey + '.tileName');
    }

    let labelTestSetting = "TEST_A"; // default setting.

    if ( componentConfig.settings.abTestSettings.packageTileNameABTestSetting ) {
        labelTestSetting = componentConfig.settings.abTestSettings.packageTileNameABTestSetting;
    }

    return subscriptionPackage.cmsKey && t(subscriptionPackage.cmsKey + '.tileName.' + labelTestSetting);
}

/**
 * Component that renders a Subscription Package in the UserRegistration's Package Selector.
 * 
 * @param {object} props 
 */
function SubscriptionPackage(props: SubscriptionPackageProps) {

    const { componentConfig } = useContext(ConfigurationContext);
    const { registrationData, setRegistrationData } = useContext(DataContext);
    const { t, i18n }: any = useTranslation(componentConfig.i18nNamespaces);
    const subscriptionPackage = props.subscriptionPackage;
    const packagePrice = subscriptionPackage.displayPrices[props.frequency];
    const isSelected = props.isSelected;
    const displaySubscriptionItemSelect = props.displaySubscriptionItemSelect;
    const closeIcon = (<MetsIcon name="svg_close" />);
    const [showPAYGDialog, setShowPAYGDialog] = useState(false);
    const className = classnames('subscription-package', { disabled: props.disabled }, { selected: isSelected });
    const selectedAgenciesCount: number|null = getSelectedAgenciesCount(packagePrice, subscriptionPackage, registrationData);
    const additionalPaymentFrequencyDescription = t('subscriptionPackage.additionalPaymentFrequencyDescription');
    const bannerTextKey = subscriptionPackage.cmsKey + '.' + props.frequency + '.bannerText';
    let bannerText = t(bannerTextKey);
    // If the banner text is equal to the key, that means there was no message found so we set the text to blank.
    if (bannerText == bannerTextKey) {
        bannerText = "";
    }

    const hasSelectedItems = () => {
        if (registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionAssociatedItemKeys) {
            return registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionAssociatedItemKeys.length > 0;
        }
        else {
            return false;
        }
    };

    const renderSubscriptionItemSelect = () => {

        return (
            <ItemSelect
                className="standout"
                disabled={!isSelected || props.disabled}
                selectedItem={registrationData.selectedSubscriptionItems[subscriptionPackage.groupKey] &&
                                registrationData.selectedSubscriptionItems[subscriptionPackage.groupKey][0] 
                    ? registrationData.selectedSubscriptionItems[subscriptionPackage.groupKey][0].toString() 
                    : null}
                items={subscriptionPackage.subscriptionItems.map((item: SubscriptionItem) => {
                    return { label: item.description, value: item.id.toString() };
                })}
                onItemSelect={(selectedItem) => {

                    const registrationDataCopy = Object.assign({}, registrationData);

                    registrationDataCopy.selectedSubscriptionItems[subscriptionPackage.groupKey] = [selectedItem.value];

                    registrationDataCopy.registrationModel.subscription.selectedGroup.groupId = subscriptionPackage.id;
                    registrationDataCopy.registrationModel.subscription.selectedGroup.cmsKey = subscriptionPackage.cmsKey;
                    registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds = [selectedItem.value];
                    registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemDescription = selectedItem.label;

                    registrationDataCopy.priceTaxesInfo.orderedPackage = selectedItem.label;

                    // update list of invoice item IDs for Order Details sidebar
                    registrationDataCopy.invoiceItemIDs = registrationDataCopy.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds;

                    setRegistrationData(registrationDataCopy);

                }}
                readOnly={subscriptionPackage.subscriptionItems.length === 1}
                placeholder={subscriptionPackage.subscriptionItems.length === 1 ? subscriptionPackage.subscriptionItems[0].description : t(subscriptionPackage.cmsKey + '.choose')}
            />
        );
    };

    return (
        <div data-testid={'subscriptionPackageDiv' + subscriptionPackage.id} className={className} data-package-id={subscriptionPackage.id} onClick={props.onClickHandler}>
            
            {isSelected && <Checkbox idPrefix={'subscriptionPackageCheckbox' + subscriptionPackage.id} checked={true} readOnly={true} />}
        
            <div className="summary">

                <div className="name">
                    {renderPackageTileName(subscriptionPackage, t, componentConfig)}
                </div>
                <div className="price">
                    <em>{packagePrice ? formatPrice(packagePrice, i18n.language, componentConfig.currencyCode, false) : t('free')}</em>
                    {packagePrice > 0 && 
                        <div className="payment-frequency-description">
                            {additionalPaymentFrequencyDescription && 
                                <span>/{ t('subscriptionPackage.additionalPaymentFrequencyDescription') }</span>
                            }
                            <span>/{ t('month') }</span>
                        </div>
                    }
                </div>
                <div className="frequency">
                    {packagePrice ? t('frequency.' + props.frequency + '.description') :
                        t('includedOrgs.list').length > 0 ? <a href="#" onClick={(e) => { setShowPAYGDialog(true); e.preventDefault(); }}>{t('freePackageFrequency')}</a> :
                        t('freePackageFrequency')}
                </div>

                {hasSelectedItems() && (selectedAgenciesCount != null) &&
                    <div className="agencies-count">
                        <span className="agencies-count-value">
                        {  
                            selectedAgenciesCount != null ? 
                            selectedAgenciesCount + ' ' + (selectedAgenciesCount === 1 ? t('agenciesCount.agency') : t('agenciesCount.agencies'))
                            : ""
                        }    
                        </span>
                    </div>
                }

                <Modal open={showPAYGDialog} onClose={() => { setShowPAYGDialog(false); }} center closeIcon={closeIcon}>
                    <h1>{t('includedOrgs.title')}</h1>
                    <p>{t('includedOrgs.desc')}</p>
                    <ul className="included-orgs-list" dangerouslySetInnerHTML={{ __html: t('includedOrgs.list')}}></ul>
                </Modal>
                
                {/* This message may be intentionally left empty on some portals */}
                <div className="information" dangerouslySetInnerHTML={{ __html: t(subscriptionPackage.cmsKey + '.information')}}></div>             

            </div>

            <SubscriptionPackagePerksList perks={subscriptionPackage.perkList || []} />
            
            {displaySubscriptionItemSelect && renderSubscriptionItemSelect()}

        </div>
    );

}

export default SubscriptionPackage;